import type { ParseKeys } from 'i18next'
import { useId, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox, Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { toOptions } from '@/components/ui/control'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useSession } from '@/features/auth/api/useSession'
import { mayReadRoles, useRoles } from '@/features/settings/api/useRoles'
import { RoleCapabilityList } from '@/features/settings/components/RoleCapabilityList'
import {
  DIRECTORY_GRANTS,
  grantState,
  ownGrants,
  withGrant,
} from '@/features/settings/userGrants'
import { userFieldError, userRefusalMessage } from '@/features/settings/userRefusals'
import { can, CAP } from '@/lib/can'
import { roleDescription, roleLabel } from '@/lib/navigation'
import type { Capability, DirectoryUser, Office, Role, RoleRecord } from '@/types/domain'

/* The account editor — the Settings «Пользователи» tab (S-7; Story 1.3 before).
 *
 * Three parts, in the order an admin reads a person's access:
 *
 *   the ROLE — picked from the server's live list, never a list written here:
 *     roles are runtime records since migration 0021, and a hard-coded five
 *     would mean a founder could create a role and never assign anybody to it;
 *   «Права роли» — what that role gives, read-only and in words, following the
 *     picker live (A-5: the three boxes alone read as everything a person may
 *     do);
 *   «Дополнительно этому сотруднику» — the three directory grants, bound to the
 *     account's OWN grants (`userCapabilities`), never its effective set — see
 *     `features/settings/userGrants.ts`.
 *
 * The draft carries the whole own set, so the save sends `capabilities` with
 * every own grant the card does not show still in it. */

export interface UserDraft {
  name: string
  email: string
  role: Role
  office: Office
  language: 'en' | 'ru'
  /** The account's OWN additive grants — every one, not only the three the
   *  card offers. What the save sends as `capabilities`. */
  capabilities: Capability[]
}

/** The two offices. A closed set rather than free text because office defaults
 *  a record's issuing entity later, and "Ashgabat " with a trailing space would
 *  silently become a third one. */
const OFFICES = ['Ashgabat', 'Yiwu'] as const

const LANGUAGES = ['ru', 'en'] as const

/* A new account's role starts EMPTY and is derived at render (`defaultRole`):
   the roles arrive after the sheet opens, and a fixed `sales` opened the form
   already refused for a manager who may not hand Sales out. */
const emptyUser: UserDraft = {
  name: '',
  email: '',
  role: '',
  office: 'Ashgabat',
  language: 'ru',
  capabilities: [],
}

export function UserFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  failure,
}: {
  open: boolean
  onClose: () => void
  record?: DirectoryUser
  onSubmit: (values: UserDraft) => void
  pending?: boolean
  /** The save's refusal, RAW — worded inside the sheet, where the reader who
   *  pressed Save is looking: the banner above the fields, and the authority
   *  rule's field codes under the role and the grants (S-7, O7). */
  failure?: unknown
}) {
  const { t } = useTranslation()
  const { data: session } = useSession()
  // Live roles, so a role a founder created this morning is assignable this
  // afternoon (migration 0021). The read is open to the account manager as
  // well as the role editor (S-7, O5).
  const roles = useRoles({ enabled: mayReadRoles(session) })
  const [draft, setDraft, touched, markTouched, dirty] = useFormDraft<UserDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            name: record.name,
            email: record.email,
            role: record.role,
            office: record.office,
            language: record.language,
            capabilities: ownGrants(record.userCapabilities),
          }
        : emptyUser,
  )

  /* The role the PICKER holds, not the stored one: «Права роли» and the boxes'
     states follow a change before anything is saved. Until the admin picks one,
     a new account shows the default role — derived, not written into the
     draft, so it neither waits for the roles read nor counts as an edit. */
  const role = draft.role || defaultRole(roles.data)
  const selectedRole: RoleRecord | undefined = roles.data?.find((r) => r.key === role)

  const nameError = touched && !draft.name.trim() ? t('common.form.required') : undefined
  // A shape check, not an RFC one: the server owns real validation, and a regex
  // strict enough to be interesting rejects addresses that are actually valid.
  const emailError =
    touched && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())
      ? t('directories.users.invalidEmail')
      : undefined
  const invalid =
    Boolean(!draft.name.trim() || emailError) || !draft.email.trim() || !role

  /* The authority rule (S-7, O7): a role wider than the actor's own authority
     is offered disabled, with the reason, and a picked one is refused before
     it is sent. The account's STORED role is exempt — an account the actor may
     edit at all holds nothing they lack. The server decides either way. */
  const roleRefused =
    selectedRole?.assignable === false && role !== record?.role
      ? t('directories.users.roleNotAssignable')
      : roles.data && !role
        ? // A new account, and no role on the list is the actor's to give.
          t('directories.users.noAssignableRole')
        : undefined
  const roleError = userFieldError(failure, 'role', t) ?? roleRefused
  const grantsError = userFieldError(failure, 'capabilities', t)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('directories.users.edit') : t('directories.users.new')}
      pending={pending}
      dirty={dirty}
      blockedReason={roleRefused}
      error={
        touched && invalid
          ? t('common.form.fixFields')
          : failure
            ? userRefusalMessage(failure, t)
            : undefined
      }
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit({ ...draft, role })
      }}
    >
      {/* «Имя», not the shared «Название»: a person has a name, a company has
          a title (A-2). */}
      <Field label={t('directories.users.name')} required error={nameError}>
        {(props) => (
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* Fixed once the account exists: an address identifies an account, and
          moving one rewrites who every historical row was attributed to. The
          server does not accept a changed address on an amendment, so the field
          is read-only here rather than silently discarded there. */}
      <Field
        label={t('directories.users.email')}
        required
        error={record ? undefined : emailError}
        hint={record ? t('directories.users.emailFixed') : undefined}
      >
        {(props) => (
          <Input
            type="email"
            inputMode="email"
            value={draft.email}
            readOnly={Boolean(record)}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* A select, not a text field: office defaults a record's issuing entity
          later, so a typo here becomes a numbering and branding problem on
          documents nobody is looking at yet. */}
      <Field label={t('directories.users.office')} required>
        {(props) => (
          <Select
            value={draft.office}
            onChange={(office) => setDraft({ ...draft, office })}
            options={toOptions(OFFICES, (office) => office)}
            {...props}
          />
        )}
      </Field>

      {/* Preferred language lives on the account rather than in the browser:
          the daily digest is composed by a worker with no request to read a
          header from (AD-27). */}
      <Field label={t('directories.users.language')} required>
        {(props) => (
          <Select
            value={draft.language}
            onChange={(language) => setDraft({ ...draft, language })}
            options={toOptions(LANGUAGES, (code) =>
              t(code === 'ru' ? 'common.language.russian' : 'common.language.english'),
            )}
            {...props}
          />
        )}
      </Field>

      {/* One option per role, built in one place. A role the actor may not
          hand out is disabled with the reason in place of its description. */}
      <Field label={t('directories.users.role')} required error={roleError}>
        {(props) => (
          <Select
            value={role}
            onChange={(next) => setDraft({ ...draft, role: next })}
            options={(roles.data ?? []).map((r) => {
              const label = roleLabel(r.key, r.name)
              // Translated only while it is still the seed: an admin's own
              // description is their words, in every language.
              const about = roleDescription(r.key, r.description)
              const refused = r.assignable === false && r.key !== record?.role
              return {
                value: r.key,
                label: label.translated ? t(label.key) : label.text,
                hint: refused
                  ? t('directories.users.roleNotAssignable')
                  : (about.translated ? t(about.key) : about.text) || undefined,
                disabled: refused,
              }
            })}
            {...props}
          />
        )}
      </Field>

      <RoleCapabilities
        role={selectedRole}
        /* isLoading, not isPending: a read this session may not make is
           disabled, and a disabled query is pending for ever. */
        isLoading={roles.isLoading}
        isError={roles.isError}
        // The pointer to «Роли и доступ» only for a reader who can open it: an
        // account manager without the role editor would be sent to a tab they
        // are not offered.
        mayEditRoles={can(session, CAP.adminRolesManage)}
      />

      {/* Own grants are additive keys on the account, never new roles (AD-7,
          FR-44) — which is why they are boxes beside the role rather than extra
          entries inside the role select. */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-text-body pb-1 text-[12.5px] leading-none">
          {t('directories.users.extraGrants')}
        </legend>
        <p className="text-text-muted -mt-1 text-[12px] leading-snug font-light">
          {t('directories.users.extraGrantsHint')}
        </p>
        {DIRECTORY_GRANTS.map((grant) => {
          const state = grantState(grant.capability, draft.capabilities, selectedRole, session)
          // The actor's refusal outranks the role's note: it is the one that
          // explains why the box will not move.
          const hint = state.refusal ?? state.note
          return (
            <Checkbox
              key={grant.capability}
              label={t(grant.labelKey)}
              hint={hint ? t(NOTE_KEYS[hint]) : undefined}
              checked={state.checked}
              disabled={state.disabled}
              onCheckedChange={(checked) =>
                setDraft((current) => ({
                  ...current,
                  capabilities: withGrant(current.capabilities, grant.capability, checked),
                }))
              }
            />
          )
        })}
        {grantsError ? (
          <p className="text-negative text-[12px] leading-snug">{grantsError}</p>
        ) : null}
      </fieldset>

      {/* Active state is deliberately not a field here. Deactivating ends every
          session the account holds and is refused for your own account, so it
          is its own act with its own confirmation — not a checkbox that takes
          effect when somebody edits a colleague's office. */}
    </FormSheet>
  )
}

/** A new account's role before the admin picks one: Sales when the actor may
 *  give it — the seed's everyday account — else the first role on the list
 *  they may give, else none (the sheet then says why it cannot save). */
function defaultRole(roles: readonly RoleRecord[] | undefined): Role {
  const assignable = (roles ?? []).filter((r) => r.assignable !== false)
  return (assignable.find((r) => r.key === 'sales') ?? assignable[0])?.key ?? ''
}

const NOTE_KEYS = {
  grantedByRole: 'directories.users.grantedByRole',
  alsoGrantedByRole: 'directories.users.alsoGrantedByRole',
  grantNotYours: 'directories.users.grantNotYours',
} as const satisfies Record<string, ParseKeys>

/** «Права роли»: the selected role's capabilities, or why there are none to
 *  show. Absent altogether for a stored role the list does not know. */
function RoleCapabilities({
  role,
  isLoading,
  isError,
  mayEditRoles,
}: {
  role: RoleRecord | undefined
  isLoading: boolean
  isError: boolean
  mayEditRoles: boolean
}) {
  const { t } = useTranslation()
  const headingId = useId()

  let body: ReactNode
  if (isError) {
    body = (
      <p className="text-text-muted text-[12.5px] font-light">
        {t('directories.users.roleCapabilitiesFailed')}
      </p>
    )
  } else if (isLoading) {
    body = (
      <p className="text-text-muted text-[12.5px] font-light">
        {t('directories.users.roleCapabilitiesLoading')}
      </p>
    )
  } else if (role) {
    body = <RoleCapabilityList role={role} />
  } else {
    return null
  }

  return (
    <div role="group" aria-labelledby={headingId} className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <p id={headingId} className="text-text-body text-[12.5px] leading-none">
          {t('directories.users.roleCapabilities')}
        </p>
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {mayEditRoles
            ? t('directories.users.roleCapabilitiesHint')
            : t('directories.users.roleCapabilitiesHintNoEditor')}
        </p>
      </div>
      {body}
    </div>
  )
}
